// Base
import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'

// Redux
import { useAppSelector } from '@redux/hooks'

// Helpers
import { useMediaQuery } from '@helpers/useMediaQuery'

// Components
import { Column, Container, Row } from '@components/app/Layout'
import Box from '@components/helpers/box/Box'
import Text from '@components/helpers/text/Text'
import SelectionTitle from '@components/selection/SelectionTitle'
import SelectionDescription from '@components/selection/SelectionDescription'
import SelectionCounter from '@components/selection/SelectionCounter'
import Flex from '@components/helpers/flex/Flex'
import DestinationCard from '@components/cards/destination/DestinationCard'
import { Button } from '@components/common/button/Buttons'
import SelectionSubtitle from '@components/selection/SelectionSubtitle'
import Grid from '@components/helpers/grid/Grid'
import Selection from '@components/selectionBox/Selection'

// Styles
import styles from './DestinationsWrapper.module.sass'

// Images
import Hero from 'public/images/photos/destination-hero.png'
import BeachImage from 'public/images/photos/beach.png'

// Icons
import { IoClose, IoLocationSharp } from 'react-icons/io5'

// Interfaces
import { DestinationData } from '@interfaces/destination'

export interface SelectedData {
  id: number
  name: string
}

const DestinationsWrapper = () => {
  const [selected, setSelected] = useState<SelectedData[]>([])

  const { destinations } = useAppSelector((state) => state.staticProps)

  const isDesktop = useMediaQuery(1168)

  const removeDestination = (id: number) => {
    setSelected((prevState) => {
      return prevState.filter((item) => item.id != id)
    })
  }

  const addDestination = (city: string, id: number) => {
    setSelected((prevState: SelectedData[]) => [
      ...prevState,
      {
        name: city,
        id
      }
    ])
  }

  return (
    <section className={styles.destinations}>
      <Image
        src={Hero}
        fill
        placeholder="blur"
        sizes="
          (min-width: 768px) 768px,
          375px
        "
        alt="Beach"
      />
      <Container>
        <Row position="center">
          <Column column={10} gutter={11}>
            <Box
              backgroundColor="white"
              borderRadius={{ sm: [16] }}
              padding={{ sm: [24], lg: [64, 96] }}
            >
              <Flex justifyContent="space-between">
                <div>
                  <SelectionTitle text="Select your destinations" />
                  <SelectionDescription text="You can select up to 3 destinations" />
                </div>
                {isDesktop && <SkipButton />}
              </Flex>

              <Row position="space-between">
                <Column column={4} gutter={3}>
                  <SelectionCounter
                    text="Suggested"
                    selected={selected.length}
                    total={3}
                  />
                  <Link href="/location">
                    <Box
                      borderRadius={{ sm: [8] }}
                      spacing={{ sm: 16 }}
                      overflow="hidden"
                      className={styles.destinations__link}
                    >
                      <Image
                        src={BeachImage}
                        placeholder="blur"
                        fill
                        sizes="
                          (min-width: 1120px) 600px,
                          375px
                        "
                        alt="Beach view"
                      />
                      <Flex center fullHeight>
                        <Text tagName="p" variant="title-12" color="white">
                          Take me everywhere
                        </Text>
                      </Flex>
                    </Box>
                  </Link>

                  <Box spacing={{ sm: 16, lg: 0 }}>
                    <Grid column={{ sm: 2 }} gutter={{ sm: 1, lg: 2 }}>
                      {destinations.map(
                        (destination: DestinationData, index: number) => (
                          <DestinationCard
                            destination={destination}
                            addDestination={addDestination}
                            removeDestination={removeDestination}
                            selected={selected.some(
                              (value) => value.id == destination.id
                            )}
                            disabled={selected.length == 3}
                            key={index}
                          />
                        )
                      )}
                    </Grid>
                  </Box>
                </Column>

                <Column column={4} gutter={3}>
                  <Flex
                    flexDirection="column"
                    fullHeight
                    justifyContent="space-between"
                  >
                    <div>
                      <Box spacing={{ lg: 32 }}>
                        {isDesktop && <SelectionSubtitle text="Search" />}
                        <Selection
                          endpoint="/api/cities/c/search"
                          placeholder="Example: Cancun"
                          listIcon={
                            <IoLocationSharp size={20} color="#6D7A80" />
                          }
                          setSelected={setSelected}
                          multiple
                          disabled={selected.length == 3}
                        />
                      </Box>

                      {!isDesktop && (
                        <ContinueButton disabled={selected.length == 0} />
                      )}

                      <SelectionSubtitle text="My destinations" />

                      {selected.length > 0 ? (
                        <Flex
                          alignItems="center"
                          columnGap={{ sm: 8 }}
                          rowGap={{ sm: 8 }}
                          wrap
                          spacing={{ lg: 24 }}
                        >
                          {selected.map((item: SelectedData) => (
                            <DestinationItem
                              name={item.name}
                              id={item.id}
                              removeDestination={removeDestination}
                              key={item.id}
                            />
                          ))}
                        </Flex>
                      ) : (
                        <Text
                          tagName="p"
                          variant="body-1"
                          color="grey"
                          spacing={{ lg: 16 }}
                        >
                          Your selected destinations will appear here
                        </Text>
                      )}
                    </div>

                    {isDesktop && (
                      <ContinueButton disabled={selected.length == 0} />
                    )}
                  </Flex>

                  {!isDesktop && <SkipButton />}
                </Column>
              </Row>
            </Box>
          </Column>
        </Row>
      </Container>
    </section>
  )
}

const ContinueButton = ({ disabled }: { disabled: boolean }) => {
  if (disabled) {
    return <></>
  } else {
    return (
      <Box spacing={{ sm: 32, lg: 0 }}>
        <Button href="/location" variant="button-3">
          Continue
        </Button>
      </Box>
    )
  }
}

const SkipButton = () => {
  return (
    <Box
      padding={{ sm: [24, 0], lg: [0] }}
      className={styles.destinations__skipButton}
    >
      <Text
        tagName="p"
        variant="body-1"
        fontWeight={600}
        color="grey"
        decoration="underline"
        align="center"
      >
        <Link href="/location">Skip</Link>
      </Text>
    </Box>
  )
}

interface DestinationItemProps {
  name: string
  id: number
  removeDestination: Function
}

const DestinationItem = ({
  name,
  id,
  removeDestination
}: DestinationItemProps) => {
  return (
    <Box
      backgroundColor="white"
      border="pink"
      padding={{ sm: [6, 16] }}
      borderRadius={{ sm: [16] }}
    >
      <Flex alignItems="center" columnGap={{ sm: 8 }}>
        <Text tagName="p" variant="body-1" fontWeight={600}>
          {name}
        </Text>
        <Flex
          onClick={() => {
            removeDestination(id)
          }}
        >
          <IoClose color="#ED6F82" size={24} style={{ marginRight: '-6px' }} />
        </Flex>
      </Flex>
    </Box>
  )
}

export default DestinationsWrapper
